// total: true — the Excel export writes a live =SUM() under this column
// (see exportExcel.js). Only set it on figures that mean something when added
// across vehicles: money amounts. Deliberately NOT set on
//   - vehicle specs      Seats, Doors, Payload, Weight, Axles, Mileage, ModelYear
//   - rates / per-day    ExchangeRate, DailyDemurrage, DailyPenalty, CreditPeriod
//   - averages, scores   AvgMonthlyPayment, PaymentPerformanceScore, MonthsSinceDelivery
//   - identifiers        VehicleID, CustomerID, BrokerID, …
//   - day counts         Days, DaysOverdue, DaysCommitted
// Several of these are typed "decimal", so without the flag the export would put
// an authoritative-looking total under Model Year. Summing an average is wrong
// outright, not merely useless.
// ── Phone rules ──────────────────────────────────────────────────────────────
// Uganda's NSN is 9 digits, so the national form is always 0 + 9 = 10 chars.
// What the leading 0 may be followed by is the whole point of these constants:
// the old rule was "^0[0-9]{9}$", which happily accepted 0070000000 — and did,
// producing a saved contact nobody could ring.
//
// The accepted families, per the UCC numbering plan:
//   07X  mobile   070/074/075 Airtel · 071 UTL · 072 Lyca · 073 Africell
//                 (defunct 2021, format still valid) · 076/077/078 MTN ·
//                 079 MTN, reallocated from Africell in March 2025
//   020  fixed    03X  fixed/VoIP (031x, 039x)
//   04X  fixed    041 Kampala · 042 Entebbe · 043 Jinja · 045 Mbale · 046-048
//
// Deliberately NOT accepted: 0800/0900 toll-free and premium (nobody's contact
// number — and the audit found none in the data), and 00/01/05/06/08/09, which
// are unassigned. If the UCC opens a new range, widening is an edit to the one
// string below plus `node scripts/gen-vehicle-fields.mjs` in the api repo.
//
// These are STRINGS compiled by new RegExp() in validation/fieldCheck.js, so a
// backslash has to survive the string literal: "\\d", never "\d". "\d" collapses
// to "d" and the rule would silently match a literal letter d — pinned by
// validation/ugPhone.test.js.
// Both alternatives stay anchored. "^$|" + a pattern that has lost its own "^"
// is a trap this file has been bitten by before (see patternFor in
// validation/fieldCheck.js on the old "|null" suffix): an unanchored
// alternative matches any string merely CONTAINING a valid number, so
// "junk0772123456" would pass.
const UG_PHONE = "^0(?:7\\d|[34]\\d|20)\\d{7}$";
const UG_PHONE_OPTIONAL = "^$|" + UG_PHONE;

// Sellers only. A Ugandan dealer's SELLERS are frequently not Ugandan: stock
// routes through Mombasa and Dar es Salaam (hence CifMombasa/EtaMombasa on
// Inventory), and the live data already holds a Kenyan and a Tanzanian seller.
// A UG number still canonicalises to the 0-form; a neighbour's stays as bare
// <cc><nsn>, which is how those rows are already stored.
//   254 Kenya · 255 Tanzania · 250 Rwanda · 257 Burundi · 211 South Sudan
const EAC_PHONE = "^0(?:7\\d|[34]\\d|20)\\d{7}$|^(?:254|255|250|257|211)\\d{8,9}$";
const EAC_PHONE_OPTIONAL = "^$|" + EAC_PHONE;

// ── Expense categories ───────────────────────────────────────────────────────
// The Expense field on an other-expense used to be free text, and the labels
// drifted exactly as you would expect: Repair beside REPAIRS, Spare beside
// Parts, bodywork in lowercase. Anything that groups by expense type — the
// sheet, the AI credit context in routes/ai.js, a future breakdown in Monthly
// Expenditures — was reading noise.
//
// ORDER IS THE MENU ORDER, and "Other" belongs last.
//
// The list is deliberately short. What the specifics were goes in the Details
// box the dialog already has, not in a fifth category.
//   Fuel           fuel of any kind
//   Tools & Spare  spare tyre, jack, spanners, accessories that ship with the car
//   Repair         repairs, and the parts bought for a repair
//   Other          ownership-transfer fees, cleaning, everything else
//
// `node/helpers/expenseRules.js` holds the server's copy. ExpenseName is not a
// vehicle field, so scripts/gen-vehicle-fields.mjs does NOT mirror it — the two
// lists are kept in step by hand. Change one, change the other.
export const EXPENSE_CATEGORIES = ["Fuel", "Tools & Spare", "Repair", "Other"];

const UG_PHONE_TOOLTIP = "Ugandan phone number: mobile 070-079, or fixed 020 / 03x / 04x, e.g 0772123456. Paste a +256, 256 or 00256 form and it is converted for you.";
const EAC_PHONE_TOOLTIP = "Ugandan number (mobile 070-079, or fixed 020 / 03x / 04x, e.g 0772123456), or a regional seller's number with its country code: 254 Kenya, 255 Tanzania, 250 Rwanda, 257 Burundi, 211 South Sudan.";

const fields = {
    DealershipID: {
        displayName: "DealershipID",
        dataType: "string",
        public: false,
        required: false,
        triggerRefresh: false
    },
    Origin: {
        displayName: "Origin",
        dataType: "string",
        public: true,
        required: false,
        triggerRefresh: false,
        tooltip: "Whether the vehicle was imported or sourced locally."
    },
    VehicleID: {
        displayName: "VehicleID",
        dataType: "integer",
        public: false,
        required: false,
        triggerRefresh: false
    },
    InvoiceNo: {
        displayName: "Invoice No",
        dataType: "string",
        public: false, //Does it appear in the table or is for internal use?
        calculated: false, //Affects whether a refetch is needed when it's modified
        regex: "^[a-zA-Z0-9_\\-]{0,30}$",
        message: "Invoice number must contain only letters, numbers, underscores, or hyphens, with a maximum of 30 characters.",
        required: false,
        triggerRefresh: false,
        tooltip: "Invoice number for the vehicle purchase."
    },
    ChassisNo: {
        displayName: "Chassis No.",
        dataType: "string",
        public: true,
        textable: true,
        bucket: "VehicleDetails",
        example: "ZNE10G-159753",
        calculated: false,
        regex: "^[a-zA-Z0-9\\-]{8,20}$",
        message: "Chassis number must be 8-20 characters long and contain only letters, numbers, or hyphens.",
        required: true,
        triggerRefresh: false,
        tooltip: "Unique chassis number / VIN of the vehicle e.g. ZNE10G-159753."
    },
    PurchaseDate: {
        displayName: "Purchase Date",
        dataType: "date",
        calculated: false,
        public: false,
        required: true,
        triggerRefresh: false,
        tooltip: "Date when the vehicle was purchased."
    },
    PurchasePrice: {
        displayName: "Purchase Price",
        dataType: "decimal",
        total: true,
        public: false,
        calculated: false,
        regex: "^[1-9][0-9]{1,10}$",
        message: "Purchase price must be a positive number with up to 11 digits, starting with a non-zero digit.",
        required: true,
        triggerRefresh: true,
        tooltip: "Price at which the vehicle was purchased. If in foreign currency, enter the exchange rate in its field."
    },
    ExchangeRate: {
        displayName: "Exchange Rate",
        dataType: "decimal",
        public: false,
        calculated: false,
        regex: "^[1-9]$|^[1-9][0-9]{1,10}$",
        message: "Exchange rate must be a positive number, starting with a non-zero digit.",
        required: true,
        triggerRefresh: true,
        tooltip: "Exchange rate used for currency conversion at time of transacting."
    },
    ModelYear: {
        displayName: "Model Year",
        dataType: "string",
        public: true,
        textable: true,
        bucket: "VehicleDetails",
        example: "2023",
        calculated: false,
        regex: "^$|^[0-9]{4,4}$",
        message: "Model year must be a 4-digit number or left empty.",
        required: false,
        triggerRefresh: false,
        tooltip: "The model year of the vehicle, e.g., 2018."
    },
    Make: {
        displayName: "Make",
        dataType: "string",
        public: true,
        textable: true,
        bucket: "VehicleDetails",
        example: "Toyota",
        calculated: false,
        //Letters-only/3-10 rejected 84 of the 508 carmakes makes, including
        //Mercedes Benz, Land Rover, Alfa Romeo and 2-letter marques (MG, DS).
        //À-ɏ covers accented Latin (Skoda, Asuna). Min 2 matches the
        //Inventory CHECK length(Make) > 1; max 30 sits under varchar(50).
        regex: "^[a-zA-Z0-9\\u00C0-\\u024F .\\-/]{2,30}$",
        maxLength: 30,
        message: "Make must be 2-30 characters and contain only letters, numbers, spaces, and . - /",
        required: true,
        triggerRefresh: false,
        tooltip: "The manufacturer of the vehicle, e.g., Toyota / Mitsubishi."
    },
    Model: {
        displayName: "Model",
        dataType: "string",
        public: true,
        textable: true,
        bucket: "VehicleDetails",
        example: "Wish",
        calculated: false,
        //carmakes model names run to 55 chars and use . , - / ( ) [ ] —
        //e.g. "Vanette Truck/Van (3rd/4th gen.)". 60 matches the widened
        //Inventory/Sales column (migration 032).
        //& : + are needed for real JDM names the market here actually sees:
        //"C-HR+", "Delica D:2", "Town & Country", "Wagon R-Wide/Wagon R+".
        regex: "^[a-zA-Z0-9\\u00C0-\\u024F .,\\-/()\\[\\]&:+]{1,60}$",
        maxLength: 60,
        message: "Model must be 1-60 characters and contain only letters, numbers, spaces, and . , - / ( ) [ ] & : +",
        required: true,
        triggerRefresh: false,
        tooltip: "The model of the vehicle, e.g., Wish / Fuso Fighter."
    },
    EngineType: {
        //Column stays EngineType; the label is just "Engine" because the field
        //now carries the code, the layout and the forced induction together.
        displayName: "Engine",
        dataType: "string",
        public: false,
        calculated: false,
        //Up to 100 chars so a dealer can write the whole thing in one place —
        //"1KD-FTV Intercooler Turbo", "V6 90° Twin Turbo MTC". The longest
        //carmakes engine_code alone is 50. ° is allowed because the source
        //engine_type uses it for cylinder-bank angles.
        regex: "^[a-zA-Z0-9\\u00B0 .,\\-/()\\[\\]<>&+]{0,100}$",
        maxLength: 100,
        message: "Engine must be up to 100 characters and contain only letters, numbers, spaces, and . , - / ( ) [ ] < > & + °",
        required: false,
        triggerRefresh: false,
        tooltip: "Engine code, layout and forced induction, e.g. 1ZZ-FE / 6D17 / 1KD-FTV Intercooler Turbo."
    },
    EngineDisplacement: {
        displayName: "Displacement (cc)",
        dataType: "integer",
        public: false,
        calculated: false,
        required: false,
        triggerRefresh: false,
        tooltip: "Engine displacement in cubic centimeters."
    },
    FuelType: {
        displayName: "Fuel Type",
        dataType: "string",
        calculated: false,
        public: false,
        //"Hybrid" alone stays for legacy rows and unknown hybrids; the
        //bracketed pair records which fuel the engine actually burns.
        //Must stay in step with the Inventory/Sales enum (migration 032).
        list: ['Petrol', 'Diesel', 'Hybrid (Petrol)', 'Hybrid (Diesel)', 'Hybrid', 'Electric'],
        required: true,
        triggerRefresh: false,
        tooltip: "Type of fuel the vehicle uses."
    },
    Drive: {
        displayName: "Drive Type",
        dataType: "string",
        calculated: false,
        public: false,
        list: ['FWD', 'RWD', 'AWD', '4WD'],
        required: false,
        triggerRefresh: false,
        tooltip: "Type of drive system the vehicle has."
    },
    Seats: {
        displayName: "Seats",
        dataType: "decimal",
        public: false,
        calculated: false,
        regex: "^[0-9]{0,2}$",
        message: "Seats must be a number up to 2 digits.",
        required: false,
        triggerRefresh: false,
        tooltip: "Number of seats in the vehicle."
    },
    Doors: {
        displayName: "Doors",
        dataType: "decimal",
        public: false,
        calculated: false,
        regex: "^[0-9]{0,1}$",
        message: "Doors must be a number less than 10.",
        required: false,
        triggerRefresh: false,
        tooltip: "Number of doors on the vehicle."
    },
    Steering: {
        displayName: "Steering",
        dataType: "string",
        calculated: false,
        public: false,
        list: ['LHD', 'RHD'],
        required: false,
        triggerRefresh: false,
        tooltip: "Steering orientation of the vehicle."
    },
    Payload: {
        displayName: "Payload Capacity (kgs)",
        dataType: "decimal",
        public: false,
        calculated: false,
        regex: "^[0-9]{0,5}$",
        message: "Payload capacity must be a number of kg up to 5 digits.",
        required: false,
        triggerRefresh: false,
        tooltip: "Payload capacity in kilograms (Especially for commercial vehicles like trucks)."
    },
    Weight: {
        displayName: "Curb Weight (kgs)",
        dataType: "decimal",
        public: false,
        calculated: false,
        regex: "^[0-9]{0,5}$",
        message: "Curb/Dry weight must be a number of kg up to 5 digits.",
        required: false,
        triggerRefresh: false,
        tooltip: "Curb/Dry weight in kilograms. Especially useful for commercial vehicles like trucks."
    },
    Axles: {
        displayName: "Axles",
        dataType: "decimal",
        public: false,
        calculated: false,
        regex: "^[0-9]{0,1}$",
        message: "Number of axles must be a number less than 10.",
        required: false,
        triggerRefresh: false,
        tooltip: "Number of axles on the vehicle (Especially useful for commercial vehicles like trucks)."
    },
    Shape: {
        displayName: "Shape",
        dataType: "string",
        public: true,
        textable: true,
        bucket: "VehicleDetails",
        example: "Truck",
        calculated: false,
        //Widened to carry carmakes body_type verbatim: all 73 distinct source
        //values fit [A-Za-z0-9 ,/()-] and the longest is 40 chars. Dealers can
        //also be more specific than the old 15-letter cap allowed.
        regex: "^[a-zA-Z0-9 ,./()\\-]{0,50}$",
        maxLength: 50,
        message: "Shape must be up to 50 characters and contain only letters, numbers, spaces, and , . - / ( ).",
        required: false,
        triggerRefresh: false,
        tooltip: "Body shape of the vehicle, e.g., Sedan, SUV, Truck."
    },
    NumberPlate: {
        displayName: "Number Plate",
        dataType: "string",
        public: true,
        textable: true,
        bucket: "VehicleDetails",
        //No space: the regex has always rejected one, but this example (which
        //also seeds the XLSX import template) contradicted it with "AB 1597NA".
        example: "AB1597NA",
        calculated: false,
        //Typed spaces are REMOVED rather than refused. "UBK 224X" is how the
        //plate appears on the car and on the logbook, so refusing it taught
        //people the field was awkward; stripping it VISIBLY, in front of them,
        //teaches the stored form instead. Declared as data, not done in a
        //handler, so the generated server mirror carries the identical rule —
        //a browser that strips while the server does not is the string-"0"
        //parity bug wearing a different hat.
        normalize: "stripSpaces",
        regex: "^[a-zA-Z0-9]{0,10}$",
        maxLength: 10,
        message: "Number plate must be up to 10 characters and contain only letters and numbers. Spaces are removed — UBK 224X is stored as UBK224X.",
        required: false,
        triggerRefresh: false,
        tooltip: "Vehicle's registration number / license plate."
    },
    Transmission: {
        displayName: "Transmission",
        dataType: "string",
        public: true,
        calculated: false,
        //5, not 3: mapTransmission emits up to "10CVT"/"10DCT", which the
        //char(5) column already accepts — this regex was the only blocker.
        regex: "^$|^[a-zA-Z0-9]{2,5}$",
        maxLength: 5,
        message: "Transmission must be 2-5 characters and contain only letters and numbers, or left empty.",
        required: false,
        triggerRefresh: false,
        tooltip: "Type of transmission the vehicle has, e.g., AT, MT6, CVT."
    },
    Mileage: {
        displayName: "Mileage (Km)",
        dataType: "decimal",
        public: true,
        calculated: false,
        regex: "^[0-9]{1,7}$",
        message: "Mileage must be a number with up to 7 digits.",
        required: false,
        triggerRefresh: false,
        tooltip: "Total mileage of the vehicle in kilometers."
    },
    Color: {
        displayName: "Color",
        dataType: "string",
        public: true,
        textable: true,
        bucket: "VehicleDetails",
        example: "White",
        calculated: false,
        regex: "^[a-zA-Z /-]{0,20}$",
        message: "Color must be up to 20 characters and contain only letters, forward slashes, hyphenes, and spaces.",
        required: false,
        triggerRefresh: false,
        tooltip: "Color of the vehicle's exterior."
    },
    Features: {
        displayName: "Extra Features",
        dataType: "string",
        public: false,
        calculated: false,
        regex: "^$|^[a-zA-Z0-9 \\-_,&.]{0,100}$",
        message: "Features must be up to 100 characters and contain only letters, numbers, spaces, hyphens, underscores, commas, ampersands, or periods.",
        required: false,
        triggerRefresh: false,
        tooltip: "Additional features of the vehicle, e.g., Sunroof, Leather Seats, AC (Air conditioning), PS (Power Steering), PW (Power Windows)."
    },
    ShippingDate: {
        displayName: "Shipping Date",
        dataType: "date",
        calculated: false,
        public: false,
        required: false,
        triggerRefresh: false,
        tooltip: "Date when the vehicle was shipped."
    },
    BLNo: {
        displayName: "BL Number",
        dataType: "string",
        public: false,
        calculated: false,
        regex: "^[A-Za-z0-9]{0,30}$",
        maxLength: 30, //column widened 20 -> 30 to match this rule (migration 032)
        message: "BL Number must be up to 30 characters and contain only letters and numbers.",
        required: false,
        triggerRefresh: false,
        tooltip: "Bill of Lading number associated with the shipment."
    },
    ShippingCost: {
        displayName: "Shipping Cost (USD)",
        dataType: "decimal",
        total: true,
        calculated: false,
        public: false,
        required: false,
        triggerRefresh: true,
        tooltip: "Cost incurred in sea freight for shipping the vehicle to the nearest port in USD."
    },
    CifMombasa: {
        displayName: "CIF To Port (USD)",
        dataType: "decimal",
        total: true,
        public: false,
        calculated: true,
        regex: "^[0-9]{1,10}$",
        message: "CIF To Port must be a number with up to 10 digits.",
        required: false,
        triggerRefresh: true,
        tooltip: "Cost, Insurance, and Freight to Mombasa / Daresalaam port in USD. This is automatically calculated based on Purchase Price, and Shipping Cost."
    },
    EtaMombasa: {
        displayName: "ETA Port",
        dataType: "date",
        calculated: false,
        public: false,
        required: false,
        triggerRefresh: false,
        tooltip: "Estimated Time of Arrival at the port."
    },
    LandTranCost: {
        displayName: "Inland Transport (USD)",
        dataType: "decimal",
        total: true,
        public: false,
        calculated: false,
        regex: "^[0-9]{1,10}$",
        message: "Inland Transport cost must be a number with up to 10 digits.",
        required: false,
        triggerRefresh: true,
        tooltip: "Cost incurred in transporting the vehicle from the port to KLA in USD."
    },
    EtaKla: {
        displayName: "ETA Kla",
        dataType: "date",
        calculated: false,
        public: false,
        required: false,
        triggerRefresh: false,
        tooltip: "Estimated Time of Arrival at Kampala bond."
    },
    ClearingCost: {
        displayName: "Clearing Cost MBA (USD)",
        dataType: "decimal",
        total: true,
        public: false,
        calculated: false,
        regex: "^[0-9]{1,10}$",
        message: "Clearing Cost must be a number with up to 10 digits.",
        required: false,
        triggerRefresh: true,
        tooltip: "Cost incurred in clearing the vehicle at the port in USD."
    },
    TargetExportPrice: {
        displayName: "Target Export Price",
        dataType: "decimal",
        total: true,
        public: false,
        calculated: false,
        regex: "^[0-9]{1,10}$",
        message: "Target Export Price must be a number with up to 10 digits.",
        required: false,
        triggerRefresh: false,
        tooltip: "Intended selling price for when the vehicle is re-exported."
    },
    ClearingUG: {
        displayName: "Clearing Cost KLA (UGX)",
        dataType: "decimal",
        total: true,
        public: false,
        calculated: false,
        regex: "^[0-9]{1,10}$",
        message: "Clearing Cost KLA must be a number with up to 10 digits.",
        required: false,
        triggerRefresh: true,
        tooltip: "Cost incurred in clearing the vehicle at Kampala bond in UGX."
    },
    WarehouseDate: {
        //Was the raw column name, which surfaced verbatim in "WarehouseDate is
        //required" — both in the modal and, since the mirror carries displayName,
        //in the server's 400s.
        displayName: "Warehouse Date",
        dataType: "date",
        calculated: false,
        public: true,
        required: true,
        //Bond fields describe a vehicle sitting in a customs bond. A locally-bought
        //car never enters one, and the picker wizard skips the bond step for Local
        //(totalSteps = Origin === "Imported" ? 7 : 6), so requiring them of a Local
        //unit left it unsavable with no field to fix. See isRequired in
        //validation/fieldCheck.js; mirrored in node/helpers/vehicleFields.js.
        requiredWhen: { field: "Origin", equals: "Imported" },
        triggerRefresh: true,
        tooltip: "Date when the vehicle was moved to the bond warehouse. This date is used to calculate demurrage."
    },
    BondName: {
        displayName: "Location",
        dataType: "string",
        public: true,
        calculated: false,
        regex: "^[a-zA-Z0-9 ]{3,30}$",
        message: "Bond Name must be 3-30 characters and contain only letters, numbers, and spaces.",
        required: true,
        requiredWhen: { field: "Origin", equals: "Imported" },
        triggerRefresh: false,
        tooltip: "Where the vehicle is kept — a bonded warehouse or the dealership's own yard."
    },
    StorageType: {
        //Which KIND of place BondName above names. A dealership that keeps stock
        //both in its own yard and in a bonded warehouse cannot answer that once
        //in Settings, because the answer differs per vehicle — and the answer is
        //what decides whether a demurrage rate is owed at all.
        //
        //Not derived from BondName. The Location field deliberately accepts free
        //text (a car may sit in a bond that is not on the dealership's list), so
        //the name cannot be trusted to imply the kind.
        //
        //`list` is here for the UI; the ALLOWLIST IS THE REGEX, because
        //checkFieldValue does not read `list`. A value outside it must fail the
        //same way on both sides of the door.
        displayName: "Storage",
        dataType: "string",
        public: false,
        calculated: false,
        list: ["Bond", "Yard"],
        regex: "^(Bond|Yard)$",
        message: "Storage must be either Bond or Yard.",
        //Never required. Every row predating this feature has it empty, and
        //helpers/storage.js resolves an empty value to the dealership's default
        //rather than refusing — demanding it would make every historical vehicle
        //unsavable the moment somebody opened it.
        required: false,
        triggerRefresh: true,
        tooltip: "Whether this vehicle sits in a bonded warehouse (where demurrage accrues) or in the dealership's own yard."
    },
    PrimaryImage: {
        displayName: "Primary Image",
        dataType: "string",
        public: false,
        calculated: false,
        message: "Primary Image URL must be a valid URL or left empty.",
        required: false,
        triggerRefresh: false,
        tooltip: "URL of the primary image of the vehicle. This is the image that shows first on the website."
    },
    CompanyAddress: {
        displayName: "Address / Bond Name",
        dataType: "string",
        public: true,
        calculated: false,
        regex: "^[a-zA-Z0-9 ]{3,30}$",
        message: "Company Address must be 3-30 characters and contain only letters, numbers, and spaces.",
        required: true,
        triggerRefresh: false,
        tooltip: "Address of the dealership or bond."
    },
    Bond: {
        displayName: "Bond Name",
        dataType: "string",
        public: true,
        calculated: false,
        regex: "^[a-zA-Z0-9 ]{3,30}$",
        message: "Bond Name must be 3-30 characters and contain only letters, numbers, and spaces.",
        required: false,
        triggerRefresh: false,
        tooltip: "Name of the bond where the vehicle is stored."
    },
    DailyDemurrage: {
        displayName: "Daily Demurrage",
        dataType: "decimal",
        public: true,
        textable: true,
        bucket: "VehicleDetails",
        example: "4,000",
        calculated: false,
        //Was "^[1-9][0-9]{1,10}$", which rejected 0 AND every single digit, so the
        //0 that blankVehicle starts with could never pass. Combined with the old
        //unconditional `required`, that made a Local vehicle unsavable: the picker
        //wizard never asks for demurrage on a Local unit, and the modal then
        //refused the 0 it had filled in itself. Now consistent with the sibling
        //cost fields (Duty, ClearingCost, LandTranCost) — presence is the
        //`requiredWhen` rule's job, and a zero on an imported unit is a
        //plausibility warning rather than a block.
        regex: "^[0-9]{1,10}$",
        message: "Daily Demurrage must be a whole number of up to 10 digits.",
        required: true,
        //TWO conditions, both of which must hold. Demurrage is what a bonded
        //customs warehouse charges to store a vehicle, so it is owed only on an
        //imported unit AND only while that unit is actually in a bond. A
        //dealership that keeps its imports in its own yard was previously forced
        //to type a figure for a bill it never receives, and there was no honest
        //value for it to enter — 0 asserts a charge of zero where in fact there
        //is no charge.
        //
        //StorageType is RESOLVED before this is evaluated, never read raw: every
        //row predating the feature has it empty, and an empty value here would
        //make this condition false and quietly drop the requirement for every
        //existing dealership. See resolveStorageType in validation/storage.js,
        //and the matching resolve step in node/helpers/vehicleRules.js.
        requiredWhen: [
            { field: "Origin", equals: "Imported" },
            { field: "StorageType", equals: "Bond" },
        ],
        triggerRefresh: true,
        tooltip: "Daily demurrage cost incurred for storing the vehicle in the bond, in UGX."
    },
    Duty: {
        displayName: "Duty",
        dataType: "decimal",
        total: true,
        public: true,
        calculated: false,
        regex: "^[0-9]{1,10}$",
        message: "Duty must be a number with up to 10 digits.",
        required: false,
        triggerRefresh: true,
        tooltip: "Import duty payable on the vehicle in UGX."
    },
    TargetCommission: {
        displayName: "Target Commission",
        dataType: "decimal",
        total: true,
        public: false,
        calculated: false,
        regex: "^[0-9]{1,10}$",
        message: "Target Commission must be a number with up to 10 digits.",
        required: false,
        triggerRefresh: false,
        tooltip: "Intended commission amount for the sale of the vehicle."
    },
    TargetPrice: {
        displayName: "Target Price",
        dataType: "decimal",
        total: true,
        public: true,
        calculated: false,
        regex: "^[0-9]{1,10}$",
        message: "Target Price must be a number with up to 10 digits.",
        required: false,
        triggerRefresh: false,
        tooltip: "Intended selling price for the vehicle. This will show on the website"
    }, /*Beyond here a re all calculated fields */
    Days: {
        displayName: "Days In Stock",
        dataType: "integer",
        calculated: true,
        public: true,
        triggerRefresh: true,
        tooltip: "Number of days the vehicle has been in stock since the Warehouse Date."
    },
    DaysCommitted: {
        displayName: "Days Committed",
        dataType: "integer",
        calculated: true,
        public: true,
        textable: true,
        bucket: "VehicleDetails",
        example: "20",
        triggerRefresh: true,
        tooltip: "Number of days since the Commitment Date."
    },
    DaysOverdue: {
        displayName: "Days Overdue",
        dataType: "integer",
        calculated: true,
        public: true,
        textable: true,
        bucket: "SaleDetails",
        example: "153",
        triggerRefresh: true,
        tooltip: "Number of days the payment is overdue since the Due Date."
    },
    StatusFlag: {
        displayName: "Status",
        dataType: "string",
        calculated: false,
        public: false,
        triggerRefresh: false
    },
    TotalDemurrage: {
        displayName: "Total Demurrage (VAT Inc.)",
        dataType: "decimal",
        total: true,
        calculated: true,
        public: true,
        tooltip: "Total demurrage cost incurred for storing the vehicle in the bond, including VAT."
    },
    OtherExpenses: { //TODO: Change to CamelCase
        displayName: "Other Expenses",
        dataType: "decimal",
        total: true,
        calculated: true,
        public: true,
        tooltip: "Sum of all additional expenses incurred for the vehicle, excluding demurrage and clearing costs. E.g fuel, repairs, maintenance, spares."
    },
    Demurrage: { //TODO: Change to CamelCase
        displayName: "Accrued Demurrage",
        dataType: "decimal",
        total: true,
        calculated: true,
        public: true,
        tooltip: "Accrued demurrage cost for the vehicle, including VAT."
    },
    TotalCost: {
        displayName: "Sub Total",
        dataType: "decimal",
        total: true,
        calculated: true,
        public: true,
        tooltip: "Landed cost BEFORE import duty: purchase, shipping, transport, clearing, demurrage, commission and other expenses. Add Duty to get Total Cost."
    },
    TotalCredit: {
        displayName: "Total Credit",
        dataType: "decimal",
        total: true,
        calculated: true,
        public: true,
        tooltip: "Total credit amount on the vehicle."
    },
    TotalPenalty: {
        displayName: "Total Penalty",
        dataType: "decimal",
        total: true,
        calculated: true,
        public: true,
        textable: true,
        bucket: "SaleDetails",
        example: "983,000",
        tooltip: "Total penalty amount incurred on the vehicle."
    },
    //Derived in the deal queries, never stored: MonthlyTrackerFee times COMPLETED
    //months since delivery. On the Credit sheet the clock is today, so it grows;
    //on CreditCleared it is frozen at the deal's last payment, so a settled deal
    //stops accruing. A deal with no recorded fee comes back NULL and renders
    //blank — that is on purpose, and why exportExcel's AS_OF_FIELDS freezes this
    //as a value rather than emitting a live formula.
    //Not textable, for the same reason as MonthlyTrackerFee: this is what the
    //DEALERSHIP has spent, not what the customer owes, so it must not be offerable
    //as a merge token in a message to that customer.
    TotalTrackerCost: {
        displayName: "Tracking Cost",
        dataType: "decimal",
        total: true,
        calculated: true,
        public: true,
        example: "450,000",
        tooltip: "Tracker rental accrued since delivery — the monthly fee times completed months. Stops when the credit is cleared."
    },
    TotalPaid: {
        displayName: "Total Paid",
        dataType: "decimal",
        total: true,
        calculated: true,
        public: true,
        textable: true,
        bucket: "SaleDetails",
        example: "28,500,000",
        tooltip: "Total amount paid towards the vehicle."
    },
    ReleaseBalance: {
        displayName: "Release Balance",
        dataType: "decimal",
        total: true,
        calculated: true,
        public: true,
        textable: true,
        bucket: "SaleDetails",
        example: "6,300,000",
        tooltip: "Balance amount required for the release of the vehicle."
    },
    TotalBalance: {
        displayName: "Total Balance",
        dataType: "decimal",
        total: true,
        calculated: true,
        public: true,
        textable: true,
        bucket: "SaleDetails",
        example: "3,400,000",
        tooltip: "Total balance amount remaining on the vehicle."
    },
    TotalCostWithDuty: {
        displayName: "Total Cost",
        dataType: "decimal",
        total: true,
        calculated: true,
        public: true,
        tooltip: "Landed cost INCLUDING import duty — Sub Total plus Duty. This is what the vehicle has cost in full, and what Gross Profit is measured against."
    },
    ListingCompleteness: {
        displayName: "Listing (%)",
        dataType: "integer",
        calculated: true,
        public: true,
        tooltip: "How much of this listing a buyer on your website can actually filter on, as a percentage of the 13 fields the site's filters use (Make, Model, Year, Body, Colour, Fuel, Transmission, Drive, Steering, Doors, Seats, Features, Price). A blank field doesn't just look untidy — a buyer who filters on it never sees this vehicle. Sort by this column to fix the worst listings first.",
    },
    TotalARPayments: {
        displayName: "Total Credit Pmts",
        dataType: "decimal",
        total: true,
        calculated: true,
        public: true,
        tooltip: "Total amount of credit payments made on the vehicle."
    },
    MonthsSinceDelivery: {
        displayName: "Months Passed",
        dataType: "decimal",
        calculated: true,
        public: true,
        tooltip: "Number of months passed since the vehicle was delivered."
    },
    AvgMonthlyPayment: {
        displayName: "Avg Monthly Pmt",
        dataType: "decimal",
        textable: true,
        bucket: "SaleDetails",
        example: "850,000",
        calculated: true,
        public: true,
        tooltip: "Average monthly payment amount made towards the vehicle."
    },
    PaymentPerformanceScore: {
        displayName: "Performance Score (%)",
        dataType: "decimal",
        textable: true,
        bucket: "SaleDetails",
        example: "85.5",
        calculated: true,
        public: true,
        tooltip: "Payment performance score as a percentage, indicating the timeliness and consistency of payments made."
    },
    DealAgreementNo: {
        displayName: "Deal Agreement No.",
        dataType: "string",
        public: false,
        calculated: false,
        regex: "^[a-zA-Z0-9\\-_]{1,20}$",
        message: "Deal Agreement Number must be 1-20 characters and contain only letters, numbers, hyphens, or underscores.",
        required: true,
        triggerRefresh: false,
        tooltip: "Deal Agreement No., most likely the serial no. from the commitment signed with the customer."
    },
    SalesInvoiceNo: {
        displayName: "Sales Agreement No.",
        dataType: "string",
        public: false,
        textable: true,
        bucket: "SaleDetails",
        example: "4450",
        calculated: false,
        regex: "^$|^[a-zA-Z0-9\\-_]{1,20}$",
        message: "Sales Agreement Number must be 1-20 characters and contain only letters, numbers, hyphens, or underscores, or left empty.",
        required: false, //Depends
        triggerRefresh: false,
        tooltip: "Sales Agreement No., most likely the serial no. from the sales agreement signed with the customer upon delivery."
    },

    DealReferenceNo: {
        displayName: "Deal Reference No",
        dataType: "string",
        public: true,
        textable: true,
        bucket: "SaleDetails",
        example: "SR 032",
        calculated: false,
        regex: "^[a-zA-Z0-9\\-_]{1,20}$",
        message: "Deal Reference Number must be 1-20 characters and contain only letters, numbers, hyphens, or underscores.",
        required: true,
        triggerRefresh: false,
        tooltip: "Reference number from credit book. Optional field."
    },
    IDType: {
        displayName: "ID Type",
        dataType: "string",
        public: true,
        calculated: false,
        list: ['National ID', 'Driving Permit', 'Other'],
        required: false,
        triggerRefresh: false,
        tooltip: "Type of identification document provided by the customer."
    },
    IDNumber: {
        displayName: "ID Number",
        dataType: "string",
        public: true,
        calculated: false,
        regex: "^[a-zA-Z0-9]{0,15}$",
        message: "ID Number must be up to 15 characters and contain only letters and numbers.",
        required: false,
        triggerRefresh: false,
        tooltip: "Identification number from the selected ID Type."
    },
    Address: {
        displayName: "Address",
        dataType: "string",
        public: true,
        calculated: false,
        regex: "^[a-zA-Z0-9- ,&.]{0,60}$",
        message: "Address must be up to 60 characters and contain only letters, numbers, hyphens, spaces, commas, ampersands, and periods.",
        required: true,
        triggerRefresh: false,
        tooltip: "Physical address."
    },
    IsCompany: {
        displayName: "Is Company",
        dataType: "boolean",
        public: false,
        calculated: false,
        required: false,
        triggerRefresh: false,
        tooltip: "Indicates whether the customer is a company or an individual."
    },
    CompanyName: {
        displayName: "Company Name",
        dataType: "string",
        public: true,
        textable: true,
        bucket: "CustomerDetails",
        example: "Movit (U) Ltd",
        calculated: false,
        regex: "^$|^[a-zA-Z0-9 &.]{3,40}$",
        message: "Company Name must be 3-40 characters and contain only letters, numbers, spaces, ampersands, and periods, or left empty.",
        required: false,
        triggerRefresh: false,
        tooltip: "Name of the company if the customer is a corporate entity."
    },
    DealershipName: {
        displayName: "Company Name",
        dataType: "string",
        public: true,
        calculated: false,
        regex: "^[a-zA-Z0-9 &.]{3,40}$",
        message: "Dealership Name must be 3-40 characters and contain only letters, numbers, spaces, ampersands, and periods.",
        required: true,
        triggerRefresh: false,
        tooltip: "Name of the dealership."
    },
    CompanyPhone1: {
        displayName: "Phone 1",
        dataType: "string",
        public: true,
        calculated: false,
        normalize: "ugPhone",
        regex: UG_PHONE,
        message: "Company Phone 1 must be a Ugandan number: 10 digits starting 07, 020, 03 or 04 (e.g 0772123456). +256 and 256 forms are converted automatically.",
        required: true,
        triggerRefresh: false,
        tooltip: UG_PHONE_TOOLTIP
    },
    CompanyPhone2: {
        displayName: "Phone 2",
        dataType: "string",
        public: true,
        calculated: false,
        normalize: "ugPhone",
        regex: UG_PHONE_OPTIONAL,
        message: "Company Phone 2 must be a Ugandan number: 10 digits starting 07, 020, 03 or 04 (e.g 0772123456), or left empty. +256 and 256 forms are converted automatically.",
        required: false,
        triggerRefresh: false,
        tooltip: UG_PHONE_TOOLTIP + " Or leave empty."
    },
    Name: {
        displayName: "Name",
        dataType: "string",
        public: true,
        calculated: false,
        regex: "^[A-Za-z ]{0,30}$",
        message: "Name must be up to 30 characters and contain only letters and spaces.",
        required: true,
        triggerRefresh: false,
        tooltip: "Full name."
    },
    FirstName: {
        displayName: "First Name",
        dataType: "string",
        public: true,
        textable: true,
        bucket: "CustomerDetails",
        example: "Mukiibi",
        calculated: false,
        regex: "^[A-Za-z ]{0,30}$",
        message: "First Name must be up to 30 characters and contain only letters and spaces.",
        required: true,
        triggerRefresh: false,
        tooltip: "First name of the individual."
    },
    LastName: {
        displayName: "Last Name",
        dataType: "string",
        public: true,
        textable: true,
        bucket: "CustomerDetails",
        example: "Moses",
        calculated: false,
        regex: "^[A-Za-z ]{0,30}$",
        message: "Last Name must be up to 30 characters and contain only letters and spaces.",
        required: true,
        triggerRefresh: false,
        tooltip: "Last name of the individual."
    },
    AccountName: {
        displayName: "Account Name",
        dataType: "string",
        public: true,
        calculated: false,
        regex: "^[A-Za-z0-9]{0,30}$",
        message: "Account Name must be up to 30 characters and contain only letters and numbers.",
        required: true,
        triggerRefresh: false,
        tooltip: "Name of the account."
    },
    ExpenseID: {
        displayName: "Expense ID",
        dataType: "string",
        public: false,
        required: true,
        triggerRefresh: false
    },
    ExpenseName: {
        displayName: "Expense",
        dataType: "string",
        public: true,
        list: EXPENSE_CATEGORIES,
        //The allowlist AS the regex, derived from the one array above so the menu
        //and the rule cannot disagree. checkFieldValue (node/helpers/vehicleRules.js)
        //tests required → maxLength → regex and has no notion of `list`, so this is
        //what makes the four categories enforceable on the server as well as here —
        //without widening a helper that FuelType, IDType, CashCredit and the currency
        //fields also run through, where a legacy off-list value would start failing.
        //Safe only while no category contains a regex metacharacter; "&" and the
        //space in "Tools & Spare" are both literals.
        regex: `^(${EXPENSE_CATEGORIES.join("|")})$`,
        message: `Expense must be one of: ${EXPENSE_CATEGORIES.join(", ")}.`,
        required: true,
        triggerRefresh: false,
        tooltip: "What kind of cost this was. Put the specifics in Details."
    },
    PaymentID: {
        displayName: "PaymentID",
        dataType: "integer",
        public: false,
        required: true,
        triggerRefresh: false,
        tooltip: "Payment method."
    },
    AdvAr: {
        displayName: "ADV/AR",
        dataType: "string",
        calculated: false,
        public: true,
        list: ['ADV', 'AR'],
        required: false,
        triggerRefresh: false,
        tooltip: "Indicates whether the payment is an advance (ADV) or accounts receivable (AR) aka credit."
    },
    PaymentDate: {
        displayName: "Payment Date",
        dataType: "date",
        public: true,
        required: true,
        triggerRefresh: false,
        tooltip: "Date on which the payment was made."
    },
    LastPmtDate: {
        displayName: "Last Paid",
        dataType: "date",
        public: true,
        textable: true,
        bucket: "SaleDetails",
        example: "25/04/2023",
        required: false,
        calculated: true,
        triggerRefresh: false,
        tooltip: "Date when the customer last made a payment."
    },
    DueDate: {
        displayName: "Due Date",
        dataType: "date",
        public: true,
        textable: true,
        bucket: "SaleDetails",
        example: "25/01/2024",
        required: true,
        triggerRefresh: false,
        tooltip: "Date when the customer was supposed to have finished clearing credit."
    },
    Amount: {
        displayName: "Amount",
        dataType: "decimal",
        total: true,
        public: true,
        //A payment of nothing is not a payment. This said false while the two
        //places that actually enforce it both said true — AddEditPayment's
        //handleSubmit refuses a blank Amount, and the import lists it in
        //validatePreview's checkRequired for the Payments tab — so only the
        //asterisk was wrong. It surfaced when the dialog started drawing its
        //asterisks from this metadata instead of hardcoding them in markup.
        required: true,
        triggerRefresh: true,
        tooltip: "Amount paid."
    },
    GrossProfit: {
        displayName: "Gross Profit",
        dataType: "decimal",
        total: true,
        public: true,
        required: false,
        triggerRefresh: true,
        tooltip: "Profit, not including administrative expenses."
    },
    Details: {
        displayName: "Details",
        dataType: "string",
        public: true,
        regex: "^[^*={}><]{0,255}$",
        message: "Details must be up to 255 characters and cannot contain *, =, {, }, >, or <.",
        required: false,
        triggerRefresh: false
    },
    CommentID: {
        displayName: "CommentID",
        dataType: "integer",
        public: false,
        required: true,
        triggerRefresh: false
    },
    EmailAddress: {
        displayName: "Email",
        dataType: "string",
        public: true,
        calculated: false,
        regex: "^[\\w.-]+@[A-Za-z0-9.-]+\\.[A-Za-z]{2,}$",
        message: "Please enter a valid email address (e.g., user@example.com).",
        required: true,
        triggerRefresh: false,
        tooltip: "The email address or name of the commenter."
    },
    Password: {
        displayName: "Password",
        dataType: "string",
        public: false,
        calculated: false,
        regex: "^[^\\n\\r]{0,20}$",
        message: "Password must be up to 20 characters and cannot contain line breaks.",
        required: true,
        triggerRefresh: false
    },
    MessagesPassword: {
        displayName: "Password",
        dataType: "string",
        public: false,
        calculated: false,
        regex: "^[^\\n\\r]{0,20}$",
        message: "Password must be up to 20 characters and cannot contain line breaks.",
        required: true,
        triggerRefresh: false,
        tooltip: "Password of your SMS account."
    },
    ConfirmPassword: {
        displayName: "Password",
        dataType: "string",
        public: false,
        calculated: false,
        regex: "^[^\\n\\r]{0,20}$",
        message: "Password must be up to 20 characters and cannot contain line breaks.",
        required: true,
        triggerRefresh: false,
        tooltip: "Confirmation of the password."
    },
    UserID: {
        displayName: "UserID",
        dataType: "integer",
        public: false,
        required: true,
        triggerRefresh: false
    },
    UserComment: {
        displayName: "Comment",
        dataType: "string",
        public: true,
        regex: "^[^*={}><]{0,2000}$",
        message: "Comment must be up to 2000 characters and cannot contain *, =, {, }, >, or <.",
        required: true,
        triggerRefresh: false,
        tooltip: "The comment text."
    },
    Remarks: {
        displayName: "Comment",
        dataType: "string",
        public: true,
        regex: "^[^*={}><]{0,255}$",
        message: "Remarks must be up to 255 characters and cannot contain *, =, {, }, >, or <.",
        //The mirror of Amount above, in the other direction: a free-text note on
        //a payment has never been mandatory. handleSubmit does not ask for it,
        //and import/mapping.js declares it `required: false`. The regex already
        //allows the empty string, so nothing enforced this — it only drew an
        //asterisk once the dialog stopped hardcoding its own.
        required: false,
        triggerRefresh: false,
        tooltip: "Additional remarks or notes regarding the payment."
    },
    CommentDate: {
        displayName: "Date",
        dataType: "datetime",
        public: true,
        required: true,
        triggerRefresh: false,
        tooltip: "Date and time when the comment was made."
    },
    AuthorName: {
        displayName: "Author",
        dataType: "string",
        public: true,
        required: false,
        triggerRefresh: false,
        tooltip: "Name of the user who made the comment."
    },
    EditedAt: {
        displayName: "Edited",
        dataType: "datetime",
        public: false,
        required: false,
        triggerRefresh: false,
        tooltip: "Date and time the comment was last edited."
    },
    EditedByName: {
        displayName: "Edited By",
        dataType: "string",
        public: false,
        required: false,
        triggerRefresh: false
    },
    DeletedAt: {
        displayName: "Deleted",
        dataType: "datetime",
        public: false,
        required: false,
        triggerRefresh: false
    },
    DeletedByName: {
        displayName: "Deleted By",
        dataType: "string",
        public: false,
        required: false,
        triggerRefresh: false
    },
    Time_Stamp: {
        displayName: "Time Stamp",
        dataType: "string",
        public: false,
        required: false,
        triggerRefresh: false
    },
    DealNo: {
        displayName: "Deal Reference No.",
        dataType: "string",
        public: true,
        textable: true,
        bucket: "SaleDetails",
        example: "PB-119",
        //Punctuation is allowed because this column's job is to match a reference
        //written on a paper file, and real ones are 'PB-119' or 'INV/2024/07'.
        //The old rule was alphanumeric-only and 12 long, which forced a backfill
        //to record 'PB119' for a file labelled 'PB-119' — a link that no longer
        //links. Must still START alphanumeric so a stray separator is not a
        //reference, and is capped at 20 because Sales.DealReferenceNo is
        //varchar(20): a longer value would be truncated by MySQL rather than
        //refused here.
        regex: "^[a-zA-Z0-9][a-zA-Z0-9 ./-]{0,19}$",
        message: "Deal Number must start with a letter or number, be at most 20 characters, and use only letters, numbers, spaces and - . /",
        required: true,
        triggerRefresh: false,
        tooltip: "Unique identifier for the sales deal. It can match the reference on your paper file, e.g. PB-119."
    },
    CommitmentDate: {
        displayName: "Commitment Date",
        dataType: "date",
        public: true,
        textable: true,
        bucket: "SaleDetails",
        example: "25/02/2025",
        required: true,
        triggerRefresh: true,
        tooltip: "Date when the customer made a commitment to purchase the vehicle by leaving a deposit."
    },
    CashCredit: {
        displayName: "Cash Or Credit",
        dataType: "string",
        calculated: false,
        public: true,
        list: ['Cash', 'Credit'],
        required: true,
        triggerRefresh: false,
        tooltip: "Indicates whether the sale was made on cash or credit basis."
    },
    AgreedPrice: {
        displayName: "Agreed Price",
        dataType: "decimal",
        total: true,
        public: true, //Apears in table??
        textable: true,
        bucket: "SaleDetails",
        example: "26,000,000",
        calculated: false,
        regex: "^[1-9][0-9]{0,9}$",
        message: "Agreed Price must be a positive number with up to 10 digits, starting with a non-zero digit.",
        required: true,
        triggerRefresh: true,
        tooltip: "Total agreed selling price of the vehicle."
    },
    Currency: {
        displayName: "Currency",
        dataType: "string",
        calculated: false,
        public: false,
        list: ['UGX', 'USD'],
        required: true,
        triggerRefresh: true,
        tooltip: "Currency in which the agreed price is denominated. Default is UGX."
    },
    PurchaseCurrency: {
        displayName: "Currency",
        dataType: "string",
        calculated: false,
        public: false,
        list: ['UGX', 'USD'],
        required: true,
        message: "Purchase Currency is required",
        triggerRefresh: true,
        tooltip: "Currency in which the purchase price is denominated. Default is USD."
    },
    ReleaseAmount: {
        displayName: "Release Amount",
        dataType: "decimal",
        total: true,
        public: true, //Apears in table??
        textable: true,
        bucket: "SaleDetails",
        example: "23,000,000",
        calculated: false,
        regex: "^[0-9]{1,10}$",
        message: "Release Amount must be a number with up to 10 digits.",
        required: true,
        triggerRefresh: true,
        tooltip: "Deposit amount required before the release of the vehicle."
    },
    CreditAmount: {
        displayName: "Credit Amount",
        dataType: "decimal",
        total: true,
        public: true, //Apears in table??
        textable: true,
        bucket: "SaleDetails",
        example: "3,000,000",
        calculated: true,
        regex: "^[0-9]{1,10}$",
        message: "Credit Amount must be a number with up to 10 digits.",
        required: true,
        tooltip: "Total credit amount on the vehicle."
    },
    BrokerID: {
        displayName: "Broker",
        dataType: "integer",
        public: false,
        required: false,
        triggerRefresh: false
    },
    SellerID: {
        displayName: "Seller",
        dataType: "integer",
        public: false,
        required: false,
        triggerRefresh: false
    },
    PurchaseBrokerID: {
        displayName: "Broker",
        dataType: "integer",
        public: false,
        required: false,
        triggerRefresh: false
    },
    MonthlyPayment: {
        displayName: "Monthly Payment",
        dataType: "decimal",
        total: true,
        public: true, //Apears in table??
        textable: true,
        bucket: "SaleDetails",
        example: "1,000,000",
        calculated: true,
        regex: "^[0-9]{1,10}$",
        message: "Monthly Payment must be a number with up to 10 digits.",
        required: false,
        tooltip: "Expected monthly payment amount from the customer."
    },
    CustomerID: {
        displayName: "Customer",
        dataType: "integer",
        public: false,
        required: true,
        triggerRefresh: false
    },
    BrokerName: {
        displayName: "Name",
        dataType: "string",
        public: false,
        calculated: false,
        regex: "^[A-Za-z ]{2,30}$",
        message: "Broker Name must be 2-30 characters and contain only letters and spaces.",
        required: false,
        triggerRefresh: false,
        tooltip: "Full name of the broker."
    },
    CustomerName: {
        displayName: "Customer",
        dataType: "string",
        public: true,
        calculated: false,
        regex: "^[A-Za-z ]{8,30}$",
        message: "Customer Name must be 8-30 characters and contain only letters and spaces.",
        required: false,
        triggerRefresh: false,
        tooltip: "Full name of the customer."
    },
    ContactPerson: {
        displayName: "Customer",
        dataType: "string",
        public: false,
        calculated: false,
        regex: "^[A-Za-z ]{8,30}$",
        message: "Contact Person must be 8-30 characters and contain only letters and spaces.",
        required: false,
        triggerRefresh: false,
        tooltip: "Full name of the contact person for the customer."
    },
    AgreedCommission: {
        displayName: "Commission",
        dataType: "decimal",
        total: true,
        public: false, //Apears in table??
        calculated: false,
        regex: "^[0-9]{1,10}$",
        message: "Agreed Commission must be a number with up to 10 digits.",
        required: false,
        triggerRefresh: true,
        tooltip: "Commission amount agreed upon for the sale of the vehicle, in UGX."
    },
    PurchaseCommission: {
        displayName: "Commission",
        dataType: "decimal",
        total: true,
        public: false, //Apears in table??
        calculated: false,
        regex: "^[0-9]{1,10}$",
        message: "Purchase Commission must be a number with up to 10 digits.",
        //NOT unconditionally required: a vehicle bought without a broker has no
        //commission, and NULL is the honest record of that. The real rule — a
        //broker obliges a commission and vice versa — is the pair check in
        //validation/vehicleRules.js, which has always been the thing enforcing it.
        //As an unconditional requirement this trapped every imported vehicle,
        //since the importer writes NULL here and the modal renders the input for
        //Local vehicles only.
        required: false,
        triggerRefresh: true,
        tooltip: `If the vehicle was purchased through a broker, this is the commission paid to the broker.`
    },
    CommissionCurrency: {
        displayName: "Commission Currency",
        dataType: "string",
        calculated: false,
        public: false,
        list: ['UGX', 'USD'],
        //Required only when a commission is actually being paid — most deals pay
        //none, and Sales.CommissionCurrency is nullable. As `required: true` this
        //demanded a currency from every deal, the same unsatisfiable-requirement
        //shape the bond trio had. The real rule is a PAIR (a commission needs a
        //currency), so it lives in validation/dealRules.js commissionNeedsCurrency
        //rather than in a requiredWhen — see the comment there for why that form
        //was not extended to cover it.
        required: false,
        message: "Commission Currency is required",
        triggerRefresh: false
    },
    CreditPeriod: {
        displayName: "Credit Period",
        dataType: "integer",
        calculated: false,
        public: false,
        textable: true,
        bucket: "SaleDetails",
        example: "3",
        //Meaningless on a cash sale, where the modal parks it at 0. Left as
        //`required: true` a payload that simply OMITS it was rejected even for a
        //cash deal, while the modal's own 0 sailed through (0 is not empty) — so
        //the flag was enforcing nothing it claimed to and something it shouldn't.
        //The ">= 1 month" part is a range rule and lives in dealRules.js.
        required: true,
        requiredWhen: { field: "CashCredit", equals: "Credit" },
        triggerRefresh: true,
        tooltip: "Duration of the credit period in months."
    },
    //The rental this dealership pays each month for the tracking device fitted to
    //a vehicle sold on credit. Snapshotted onto the deal at sale time, so changing
    //the Settings default never rewrites deals already written.
    //
    //`public: true` because this is the rate the Credit sheet shows beside the
    //accrued TotalTrackerCost — a total nobody can check is a total nobody trusts.
    //No `total: true`: it is a rate, and the header of this file rules that rates
    //are never summed across vehicles (DailyPenalty, DailyDemurrage and
    //CreditPeriod all decline it for the same reason).
    //
    //Deliberately NOT requiredWhen CashCredit === "Credit". Plenty of credit deals
    //carry no tracker, and the sell modal DISABLES this input on a cash deal — an
    //error pointing at a disabled input is a dead end the wizard has already been
    //bitten by. A stale value is cleared on the switch to Cash instead.
    //NOT textable, and NOT bucketed. `textable` is what offers a field as a merge
    //token in MessageTemplateEditor, i.e. in a message sent TO THE CUSTOMER. This
    //is the DEALERSHIP's own cost, not money the customer owes — putting it in a
    //customer message would read as a charge. TotalPenalty is textable because a
    //penalty genuinely is owed; that is the difference.
    //
    //8 integer digits, not 10: the column is DECIMAL(10,2), which holds ten TOTAL
    //digits — max 99999999.99. A 10-digit rule accepted values the column cannot
    //store, and the two write paths then disagreed about it. The procedure's
    //CAST(... AS DECIMAL(10,2)) silently CLAMPS 9999999999 to 99999999.99 — 1% of
    //what was typed, stored with no error — while the inline sheet edit does a
    //plain UPDATE and dies on ER_WARN_DATA_OUT_OF_RANGE. Same input, two wrong
    //answers. (DailyPenalty and DailyDemurrage still carry the loose 10-digit rule
    //against the same column type; same latent bug, left alone as out of scope.)
    MonthlyTrackerFee: {
        displayName: "Monthly Tracker Fee",
        dataType: "decimal",
        calculated: false,
        public: true,
        example: "150,000",
        required: false,
        regex: "^[0-9]{1,8}(\\.[0-9]{1,2})?$",
        message: "Monthly Tracker Fee must be a number with up to 8 digits, and at most 2 decimal places.",
        tooltip: "Monthly rental charged for this vehicle's tracking device while the credit runs."
    },
    DefaultCreditPeriod: {
        displayName: "Default Credit Period",
        dataType: "integer",
        calculated: false,
        public: false,
        required: false,
        tooltip: "Default duration of the credit period in months."
    },
    //Settings-only, and it needs a name of its own rather than reusing
    //MonthlyTrackerFee: this file is ONE registry keyed by column name across every
    //table, so the Dealerships default and the Sales column cannot share a key.
    //Named for its neighbours DefaultCreditPeriod / DefaultDownPayment.
    DefaultTrackerFee: {
        displayName: "Default Tracker Fee",
        dataType: "decimal",
        calculated: false,
        public: false,
        required: false,
        regex: "^[0-9]{1,8}(\\.[0-9]{1,2})?$",
        message: "Default Tracker Fee must be a number with up to 8 digits, and at most 2 decimal places.",
        tooltip: "Monthly tracker rental suggested for new credit deals."
    },
    DepositAmount: {
        displayName: "Deposit",
        dataType: "decimal",
        total: true,
        public: true, //Apears in table??
        calculated: false,
        regex: "^[0-9]{1,10}$",
        message: "Deposit Amount must be a number with up to 10 digits.",
        required: true,
        triggerRefresh: true,
        tooltip: "Initial deposit amount paid by the customer upon commitment."
    },
    DefaultDownPayment: {
        displayName: "Deposit",
        dataType: "decimal",
        public: true, //Apears in table??
        calculated: false,
        regex: "^[0-9]{1,2}$",
        message: "Deposit Amount must be a percentage.",
        tooltip: "The default percentage of the vehicle price that is paid as a down payment."
    },
    DailyPenalty: {
        displayName: "Daily Penalty",
        dataType: "decimal",
        calculated: false,
        regex: "^[0-9]{1,10}$",
        message: "Daily Penalty must be a number with up to 10 digits.",
        required: false,
        tooltip: "Daily penalty amount incurred for late payments on the vehicle."
    },
    PaymentMethodID: {
        displayName: "Payment Method",
        dataType: "integer",
        public: false,
        required: true,
        triggerRefresh: false
    },
    // Phone1/Phone2 carry the rule for CUSTOMERS and BROKERS. Sellers use the
    // Phone1_Seller/Phone2_Seller entries below — same column name, wider rule.
    // contactRules.js on the server and the modals pick per table.
    Phone1: {
        displayName: "Phone 1",
        dataType: "string",
        public: true,
        textable: true,
        bucket: "CustomerDetails",
        example: "0752761111",  // Changed from "256752761111"
        calculated: false,
        normalize: "ugPhone",
        regex: UG_PHONE,
        message: "Phone 1 must be a Ugandan number: 10 digits starting 07, 020, 03 or 04 (e.g 0772123456). +256 and 256 forms are converted automatically.",
        required: true,
        triggerRefresh: false,
        tooltip: UG_PHONE_TOOLTIP
    },
    Phone2: {
        displayName: "Phone 2",
        dataType: "string",
        public: true,
        textable: true,
        bucket: "CustomerDetails",
        example: "0785761111",  // Changed from "256785761111"
        calculated: false,
        normalize: "ugPhone",
        regex: UG_PHONE_OPTIONAL,
        message: "Phone 2 must be a Ugandan number: 10 digits starting 07, 020, 03 or 04 (e.g 0772123456), or left empty. +256 and 256 forms are converted automatically.",
        required: false,
        triggerRefresh: false,
        tooltip: UG_PHONE_TOOLTIP + " Or leave empty."
    },
    // The Sellers variants of Phone1/Phone2. The suffix follows the convention
    // already used for Phone_TeamMember and Phone_DealershipLocation: same
    // column, a rule that differs by where it is used.
    Phone1_Seller: {
        displayName: "Phone 1",
        dataType: "string",
        public: true,
        textable: true,
        bucket: "CustomerDetails",
        example: "0752761111",
        calculated: false,
        normalize: "eacPhone",
        regex: EAC_PHONE,
        message: "Phone 1 must be a Ugandan number (10 digits starting 07, 020, 03 or 04, e.g 0772123456) or a regional one with its country code (254 Kenya, 255 Tanzania, 250 Rwanda, 257 Burundi, 211 South Sudan).",
        required: true,
        triggerRefresh: false,
        tooltip: EAC_PHONE_TOOLTIP
    },
    Phone2_Seller: {
        displayName: "Phone 2",
        dataType: "string",
        public: true,
        textable: true,
        bucket: "CustomerDetails",
        example: "0785761111",
        calculated: false,
        normalize: "eacPhone",
        regex: EAC_PHONE_OPTIONAL,
        message: "Phone 2 must be a Ugandan number (10 digits starting 07, 020, 03 or 04, e.g 0772123456) or a regional one with its country code (254 Kenya, 255 Tanzania, 250 Rwanda, 257 Burundi, 211 South Sudan), or left empty.",
        required: false,
        triggerRefresh: false,
        tooltip: EAC_PHONE_TOOLTIP + " Or leave empty."
    },
    ContactPhone1: {
        displayName: "Contact Phone 1",
        dataType: "string",
        public: true,
        calculated: false,
        normalize: "ugPhone",
        regex: UG_PHONE,
        message: "Contact Phone 1 must be a Ugandan number: 10 digits starting 07, 020, 03 or 04 (e.g 0414123456). +256 and 256 forms are converted automatically.",
        required: true,
        triggerRefresh: false,
        tooltip: UG_PHONE_TOOLTIP
    },
    ContactPhone2: {
        displayName: "Contact Phone 2",
        dataType: "string",
        public: true,
        calculated: false,
        normalize: "ugPhone",
        regex: UG_PHONE_OPTIONAL,
        message: "Contact Phone 2 must be a Ugandan number: 10 digits starting 07, 020, 03 or 04 (e.g 0414123456), or left empty. +256 and 256 forms are converted automatically.",
        required: false,
        triggerRefresh: false,
        tooltip: UG_PHONE_TOOLTIP + " Or leave empty."
    },
    DeliveryDate: {
        displayName: "Delivery Date",
        dataType: "date",
        public: true,
        textable: true,
        bucket: "SaleDetails",
        example: "05/05/2025",
        required: false, //Depends
        triggerRefresh: false,
        tooltip: "Date when the vehicle was delivered to the customer."
    },
    WriteOffDate: {
        displayName: "Writeoff Date",
        dataType: "date",
        public: false,
        textable: false,
        bucket: "SaleDetails",
        example: "05/05/2025",
        required: false, //Depends
        triggerRefresh: false,
        tooltip: "Date when the vehicle's credit balance was written off."
    },

    WriteOffReason: {
        displayName: "Write Off Reason",
        dataType: "string",
        public: true,
        calculated: false,
        regex: "^[\\s\\S]{1,2000}$",
        message: "Reason is required (up to 255 characters).",
        required: true,
        triggerRefresh: false,
        tooltip: "Reason for writing off the vehicle's credit balance."
    },

    WrittenOffBy: {
        displayName: "WriteOff Executor",
        dataType: "integer",
        public: false,
        required: false,
        triggerRefresh: false,
        tooltip: "Person who executed the write-off."
    },

    FoundingYear: {
        displayName: "Founding Year",
        dataType: "number",
        public: true,
        calculated: false,
        regex: "^([1][8-9][0-9]{2}|[2][0][0-9]{2})?$|^$", // Allows empty or years 1800-2099
        message: "Please enter a valid four-digit year (e.g., 2005).",
        required: false,
        triggerRefresh: false,
        tooltip: "Year the dealership was founded. For the website."
    },
    Mission: {
        displayName: "Mission Statement",
        dataType: "string",
        public: true,
        calculated: false,
        regex: "^[\\s\\S]{0,1000}$", // Allows any characters up to 1000, including newlines
        message: "Mission statement can be up to 1000 characters.",
        required: false,
        triggerRefresh: false,
        tooltip: "Mission statement of the dealership. For the website."
    },
    History: {
        displayName: "Dealership His For the website.tory",
        dataType: "string",
        public: true,
        calculated: false,
        regex: "^[\\s\\S]{0,5000}$", // Allows any characters up to 5000
        message: "History can be up to 5000 characters.",
        required: false,
        triggerRefresh: false,
        tooltip: "History of the dealership. For the website."
    },
    CustomerSatisfaction: {
        displayName: "Customer Satisfaction (%)",
        dataType: "number",
        public: true,
        calculated: false,
        regex: "^([0-9]|[1-9][0-9]|100)?$|^$", // Allows empty or 0-100
        message: "Customer satisfaction should be a number between 0 and 100.",
        required: false,
        triggerRefresh: false,
        tooltip: "Customer satisfaction rating as a percentage. For the website."
    },
    TeamSize: {
        displayName: "Team Size",
        dataType: "number",
        public: true,
        calculated: false,
        regex: "^[1-9]\\d{0,4}$|^$", // Allows empty or a positive integer up to 5 digits
        message: "Team size must be a positive number.",
        required: false,
        triggerRefresh: false,
        tooltip: "Number of employees in the dealership. For the website."
    },

    // New fields for Inventory & Sales Tables (shared properties)
    IsFeatured: {
        displayName: "Is Featured",
        dataType: "boolean",
        public: false,
        calculated: false,
        regex: "^(true|false|0|1)$",
        message: "Must be true or false.",
        required: true, // NOT NULL in schema
        triggerRefresh: true,
        tooltip: "Whether the vehicle is featured on the front page of the website."
    },
    FeaturedUntil: {
        displayName: "Featured Until",
        dataType: "date",
        public: false,
        calculated: false,
        required: false,
        triggerRefresh: true,
        tooltip: "Date until which the vehicle remains featured on the website front page."
    },
    FeaturedSortOrder: {
        displayName: "Featured Sort Order",
        dataType: "number",
        public: false, // Often an admin-only field for ordering
        calculated: false,
        regex: "^\\d{1,5}$|^$", // Integer up to 5 digits or empty
        message: "Sort order must be a number.",
        required: false, // Has DEFAULT 0
        triggerRefresh: true,
        tooltip: "Sort order for featured vehicles; lower numbers appear first."
    },
    Description: {
        displayName: "Description",
        dataType: "string",
        public: false,       // keeps it out of the table/sheet column view
        calculated: false,
        regex: "^[\\s\\S]{0,2000}$|^$", // Up to 2000 chars (textarea — multiline OK)
        message: "Description can be up to 2000 characters.",
        required: false,
        triggerRefresh: false,
        tooltip: "Full description shown on the vehicle page of your website."
    },
    FeaturedDescription: {
        displayName: "Featured Description",
        dataType: "string",
        public: false,
        calculated: false,
        regex: "^.{0,255}$|^$", // Max 255 chars or empty
        message: "Description can be up to 255 characters.",
        required: false,
        triggerRefresh: false,
        tooltip: "Description shown when the vehicle is featured on the website."
    },
    IsDiscounted: {
        displayName: "Is Discounted",
        dataType: "boolean",
        public: false,
        calculated: false,
        regex: "^(true|false|0|1)$",
        message: "Must be true or false.",
        required: true, // NOT NULL in schema
        triggerRefresh: true,
        tooltip: "Marks the vehicle as discounted on the website for promotional purposes. The vehicle also shows up under the promotions section and gets a 'discounted' badge."
    },
    DiscountAmount: {
        displayName: "Discount Amount",
        dataType: "decimal",
        total: true,
        public: false,
        calculated: false,
        required: false, // Has DEFAULT '0.00'
        triggerRefresh: true,
        tooltip: "Amount or percentage of discount applied to the vehicle price."
    },
    DiscountType: {
        displayName: "Discount Type",
        dataType: "string", // Represents ENUM
        public: false,
        calculated: false,
        options: ["Percentage", "FixedAmount"], // Useful for dropdowns
        required: false, // Allows NULL
        triggerRefresh: true,
        tooltip: "Type of discount applied: 'Percentage' for percentage-based discounts, 'FixedAmount' for fixed monetary discounts."
    },
    DiscountStart: {
        displayName: "Discount Start Date",
        dataType: "date",
        public: false,
        calculated: false,
        required: false,
        triggerRefresh: true,
        tooltip: "Date when the discount becomes active."
    },
    DiscountEnd: {
        displayName: "Discount End Date",
        dataType: "date",
        public: false,
        calculated: false,
        required: false,
        triggerRefresh: true,
        tooltip: "Date when the discount expires."
    },
    OriginalPrice: {
        displayName: "Original Price",
        dataType: "decimal",
        total: true,
        public: false,
        calculated: false,
        required: false,
        triggerRefresh: true,
        tooltip: "Original price of the vehicle before any discounts were applied."
    },
    PromotionName: { // This also exists in Promotions table with NOT NULL
        displayName: "Promotion Name (Applied)",
        dataType: "string",
        public: false,
        calculated: false,
        regex: "^.{0,100}$|^$", // Max 100 chars or empty
        message: "Promotion name can be up to 100 characters.",
        required: false, // In Inventory/Sales, it can be NULL
        triggerRefresh: false,
        tooltip: "Name of the promotion applied to the vehicle."
    },
    PromotionDescription: {
        displayName: "Promotion Description",
        dataType: "string",
        public: false,
        calculated: false,
        regex: "^.{0,255}$|^$", // Max 255 chars or empty
        message: "Description can be up to 255 characters.",
        required: false,
        triggerRefresh: false,
        tooltip: "Description of the promotion applied to the vehicle."
    },
    // Fields for DealershipLocations Table
    LocationID: {
        displayName: "Location ID",
        dataType: "number",
        public: false, // Typically not directly user-editable
        calculated: false, // As it's AUTO_INCREMENT
        regex: "^\\d+$",
        message: "Location ID is a number.",
        required: false,
        triggerRefresh: false
    },
    // DealershipID is a foreign key, often handled internally
    LocationName: {
        displayName: "Location Name",
        dataType: "string",
        public: true,
        calculated: false,
        regex: "^.{1,100}$",
        message: "Location name is required (up to 100 characters).",
        required: true,
        triggerRefresh: false,
        tooltip: "Name of the dealership location."
    },
    Address: {
        displayName: "Address",
        dataType: "string",
        public: true,
        calculated: false,
        regex: "^.{1,255}$",
        message: "Address is required (up to 255 characters).",
        required: true,
        triggerRefresh: false,
        tooltip: "Physical address of the dealership location."
    },
    Phone_DealershipLocation: { // Added suffix to distinguish from other Phone fields
        displayName: "Location Phone",
        dataType: "string",
        public: true,
        calculated: false,
        regex: "^[+]?[0-9\\s\\-\\(\\)]{7,20}$|^$", // Allows various phone formats, or empty
        message: "Enter a valid phone number (7-20 digits, can include +, spaces, hyphens).",
        required: false,
        triggerRefresh: false,
        tooltip: "Contact phone number for the dealership location."
    },
    Email_DealershipLocation: { // Added suffix
        displayName: "Location Email",
        dataType: "string",
        public: true,
        calculated: false,
        regex: "^([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,})?$|^$", // Standard email regex or empty
        message: "Enter a valid email address.",
        required: false,
        triggerRefresh: false,
        tooltip: "Contact email address for the dealership location."
    },
    IsMainLocation: {
        displayName: "Is Main Location",
        dataType: "boolean",
        public: true,
        calculated: false,
        regex: "^(true|false|0|1)$",
        message: "Specify if this is the main location.",
        required: true,
        triggerRefresh: false,
        tooltip: "Indicates whether this location is the main dealership location."
    },
    LocationType: {
        displayName: "Location Type",
        dataType: "string", // ENUM
        public: true,
        calculated: false,
        regex: "^(Showroom|Service|Parts|Office)$",
        options: ["Showroom", "Service", "Parts", "Office"],
        message: "Select a valid location type.",
        required: true,
        triggerRefresh: false,
        tooltip: "Type of dealership location (e.g., Showroom, Service Center)."
    },
    OpeningHours: {
        displayName: "Opening Hours",
        dataType: "string", // TEXT field, could be JSON string for structured data
        public: true,
        calculated: false,
        regex: "^[\\s\\S]{0,1000}$|^$", // Up to 1000 chars or empty
        message: "Opening hours details can be up to 1000 characters.",
        required: false,
        triggerRefresh: false,
        tooltip: "Operating hours for the dealership location."
    },
    MapCoordinates: {
        displayName: "Map Coordinates (Lat,Lng)",
        dataType: "string",
        public: true,
        calculated: false,
        regex: "^-?\\d{1,3}\\.\\d+,-?\\d{1,3}\\.\\d+$|^$", // Basic lat,lng or empty
        message: "Enter valid map coordinates (e.g., 0.3476,32.5825).",
        required: false,
        triggerRefresh: false,
        tooltip: "Geographical coordinates for map display (e.g., 0.3476,32.5825)."
    },

    // Fields for TeamMembers Table
    MemberID: {
        displayName: "Member ID",
        dataType: "number",
        public: false,
        calculated: false,
        regex: "^\\d+$",
        message: "Member ID is a number.",
        required: false,
        triggerRefresh: false
    },
    Name_TeamMember: { // Suffix for clarity
        displayName: "Team Member Name",
        dataType: "string",
        public: true,
        calculated: false,
        regex: "^.{1,100}$",
        message: "Team member name is required (up to 100 characters).",
        required: true,
        triggerRefresh: false,
        tooltip: "Name of the team member."
    },
    Position: {
        displayName: "Position",
        dataType: "string",
        public: true,
        calculated: false,
        regex: "^.{1,100}$",
        message: "Position is required (up to 100 characters).",
        required: true,
        triggerRefresh: false,
        tooltip: "Position or role of the team member within the dealership."
    },
    Bio: {
        displayName: "Bio",
        dataType: "string",
        public: true,
        calculated: false,
        regex: "^[\\s\\S]{0,2000}$|^$", // Up to 2000 chars or empty
        message: "Bio can be up to 2000 characters.",
        required: false,
        triggerRefresh: false,
        tooltip: "Short biography of the team member."
    },
    ImageURL_TeamMember: { // Suffix
        displayName: "Member Image URL",
        dataType: "string",
        public: true,
        calculated: false,
        regex: "^(https?://[\\w\\d./?=#&%-]+)?$|^$", // Basic URL or empty
        message: "Enter a valid URL for the image.",
        required: false,
        triggerRefresh: false,
        tooltip: "URL of the team member's image."
    },
    Email_TeamMember: { // Suffix
        displayName: "Member Email",
        dataType: "string",
        public: true,
        calculated: false,
        regex: "^([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,})?$|^$",
        message: "Enter a valid email address.",
        required: false,
        triggerRefresh: false,
        tooltip: "Email address of the team member."
    },
    Phone_TeamMember: { // Suffix
        displayName: "Member Phone",
        dataType: "string",
        public: true,
        calculated: false,
        regex: "^[+]?[0-9\\s\\-\\(\\)]{7,20}$|^$",
        message: "Enter a valid phone number.",
        required: false,
        triggerRefresh: false,
        tooltip: "Phone number of the team member."
    },
    SortOrder_TeamMember: { // Suffix
        displayName: "Sort Order",
        dataType: "number",
        public: false,
        calculated: false,
        regex: "^\\d{1,5}$|^$",
        message: "Sort order must be a number.",
        required: false, // Has DEFAULT 0
        triggerRefresh: true,
        tooltip: "Sort order for displaying team members; lower numbers appear first."
    },
    IsActive_TeamMember: { // Suffix
        displayName: "Is Active",
        dataType: "boolean",
        public: true,
        calculated: false,
        regex: "^(true|false|0|1)$",
        message: "Specify if the team member is active.",
        required: true,
        triggerRefresh: true,
        tooltip: "Indicates whether the team member is currently active."
    },

    // Fields for DealershipAwards Table
    AwardID: {
        displayName: "Award ID",
        dataType: "number",
        public: false,
        calculated: false,
        regex: "^\\d+$",
        message: "Award ID is a number.",
        required: false,
        triggerRefresh: false,
    },
    AwardName: {
        displayName: "Award Name",
        dataType: "string",
        public: true,
        calculated: false,
        regex: "^.{1,255}$",
        message: "Award name is required (up to 255 characters).",
        required: true,
        triggerRefresh: false,
        tooltip: "Name of the award received by the dealership."
    },
    AwardingBody: {
        displayName: "Awarding Body",
        dataType: "string",
        public: true,
        calculated: false,
        regex: "^.{0,100}$|^$",
        message: "Awarding body can be up to 100 characters.",
        required: false,
        triggerRefresh: false,
        tooltip: "Organization or body that granted the award."
    },
    AwardYear: {
        displayName: "Award Year",
        dataType: "number",
        public: true,
        calculated: false,
        regex: "^([1][8-9][0-9]{2}|[2][0][0-9]{2})?$|^$",
        message: "Please enter a valid four-digit year.",
        required: false,
        triggerRefresh: false,
        tooltip: "Year the award was received."
    },
    Description_DealershipAward: { // Suffix
        displayName: "Award Description",
        dataType: "string",
        public: true,
        calculated: false,
        regex: "^[\\s\\S]{0,1000}$|^$",
        message: "Description can be up to 1000 characters.",
        required: false,
        triggerRefresh: false,
        tooltip: "Description or details about the award."
    },
    ImageURL_DealershipAward: { // Suffix
        displayName: "Award Image URL",
        dataType: "string",
        public: true,
        calculated: false,
        regex: "^(https?://[\\w\\d./?=#&%-]+)?$|^$",
        message: "Enter a valid URL for the image.",
        required: false,
        triggerRefresh: false,
        tooltip: "URL of the image representing the award."
    },

    // Fields for CustomerTestimonials Table
    TestimonialID: {
        displayName: "Testimonial ID",
        dataType: "number",
        public: false,
        calculated: false,
        regex: "^\\d+$",
        message: "Testimonial ID is a number.",
        required: false,
        triggerRefresh: false
    },
    CustomerName: {
        displayName: "Customer Name",
        dataType: "string",
        public: true,
        calculated: false,
        regex: "^.{1,100}$",
        message: "Customer name is required (up to 100 characters).",
        required: true,
        triggerRefresh: false,
        tooltip: "Name of the customer providing the testimonial."
    },
    Comment: {
        displayName: "Comment",
        dataType: "string",
        public: true,
        calculated: false,
        regex: "^[\\s\\S]{1,2000}$",
        message: "Comment is required (up to 2000 characters).",
        required: true,
        triggerRefresh: false,
        tooltip: "Customer's testimonial comment about their experience."
    },
    Rating: {
        displayName: "Rating (1-5)",
        dataType: "number",
        public: true,
        calculated: false,
        regex: "^[1-5]$", // Check constraint is 1-5
        message: "Rating must be between 1 and 5.",
        required: true,
        triggerRefresh: false,
        tooltip: "Customer's rating of their experience, from 1 (lowest) to 5 (highest)."
    },
    Date_CustomerTestimonial: { // Suffix
        displayName: "Testimonial Date",
        dataType: "date",
        public: true,
        calculated: false,
        regex: "^(\\d{4}-\\d{2}-\\d{2})?$|^$",
        message: "Please use YYYY-MM-DD format for the date.",
        required: false,
        triggerRefresh: false,
        tooltip: "Date when the testimonial was submitted."
    },
    Approved: {
        displayName: "Approved",
        dataType: "boolean",
        public: true, // Admin might see/edit this in a table
        calculated: false,
        regex: "^(true|false|0|1)$",
        message: "Specify if the testimonial is approved.",
        required: true,
        triggerRefresh: true,
        tooltip: "Indicates whether the testimonial has been approved for display on the website."
    },
    CustomerImage: {
        displayName: "Customer Image URL",
        dataType: "string",
        public: true,
        calculated: false,
        regex: "^(https?://[\\w\\d./?=#&%-]+)?$|^$",
        message: "Enter a valid URL for the customer's image.",
        required: false,
        triggerRefresh: false
    },
    VehiclePurchased: {
        displayName: "Vehicle Purchased",
        dataType: "string",
        public: true,
        calculated: false,
        regex: "^.{0,100}$|^$",
        message: "Vehicle purchased can be up to 100 characters.",
        required: false,
        triggerRefresh: false,
        tooltip: "Details of the vehicle purchased by the customer."
    },

    // Fields for Promotions Table
    PromotionID: {
        displayName: "Promotion ID",
        dataType: "number",
        public: false,
        calculated: false,
        regex: "^\\d+$",
        message: "Promotion ID is a number.",
        required: false,
        triggerRefresh: false,
    },
    PromotionName_PromotionsTable: { // Suffix to distinguish from Inventory.PromotionName if flat
        displayName: "Promotion Campaign Name",
        dataType: "string",
        public: true,
        calculated: false,
        regex: "^.{1,100}$",
        message: "Promotion name is required (up to 100 characters).",
        required: true,
        triggerRefresh: false
    },
    Description_Promotion: { // Suffix
        displayName: "Promotion Description",
        dataType: "string",
        public: true,
        calculated: false,
        regex: "^[\\s\\S]{0,1000}$|^$",
        message: "Description can be up to 1000 characters.",
        required: false,
        triggerRefresh: false
    },
    StartDate_Promotion: { // Suffix
        displayName: "Promotion Start Date",
        dataType: "date",
        public: true,
        calculated: false,
        regex: "^\\d{4}-\\d{2}-\\d{2}$",
        message: "Start date is required (YYYY-MM-DD).",
        required: true,
        triggerRefresh: true
    },
    EndDate_Promotion: { // Suffix
        displayName: "Promotion End Date",
        dataType: "date",
        public: true,
        calculated: false,
        regex: "^\\d{4}-\\d{2}-\\d{2}$",
        message: "End date is required (YYYY-MM-DD).",
        required: true,
        triggerRefresh: true
    },
    DiscountType_Promotion: { // Suffix
        displayName: "Promotion Discount Type",
        dataType: "string", // ENUM
        public: true,
        calculated: false,
        regex: "^(Percentage|FixedAmount)$",
        options: ["Percentage", "FixedAmount"],
        message: "Select a valid discount type for the promotion.",
        required: true,
        triggerRefresh: true
    },
    DiscountAmount_Promotion: { // Suffix
        displayName: "Promotion Discount Amount",
        dataType: "number",
        public: true,
        calculated: false,
        regex: "^\\d{1,8}(\\.\\d{1,2})?$", // Decimal(10,2)
        message: "Enter a valid discount amount for the promotion.",
        required: true,
        triggerRefresh: true
    },
    AppliesTo: {
        displayName: "Applies To",
        dataType: "string", // ENUM
        public: true,
        calculated: false,
        regex: "^(AllVehicles|SelectedVehicles|SpecificMakes|SpecificModels)$",
        options: ["AllVehicles", "SelectedVehicles", "SpecificMakes", "SpecificModels"],
        message: "Specify what the promotion applies to.",
        required: true,
        triggerRefresh: false
    },
    FilterCriteria: {
        displayName: "Filter Criteria (JSON)",
        dataType: "string", // JSON stored as text
        public: false, // Complex, likely handled by custom UI
        calculated: false,
        regex: "^[{].*[}]$|^$", // Basic JSON object check or empty
        message: "Filter criteria must be a valid JSON object or empty.",
        required: false,
        triggerRefresh: false
    },
    IsActive_Promotion: { // Suffix
        displayName: "Is Promotion Active",
        dataType: "boolean",
        public: true,
        calculated: false,
        regex: "^(true|false|0|1)$",
        message: "Specify if the promotion is active.",
        required: true,
        triggerRefresh: true
    },
}

export default fields;